import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { WorkerAttr } from '@/types/worker-attr';
import { cn } from '@/utils/classnames';

import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from '@tiptap/suggestion';

type TabFilter = 'worker' | 'company' | 'brand' | null;

export const WorkerAttrSuggestionList = forwardRef<
  { onKeyDown: (props: SuggestionKeyDownProps) => boolean },
  SuggestionProps<WorkerAttr>
>(function WorkerAttrSuggestionList(props, ref) {
  const intl = useIntl();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [tabFilter, setTabFilter] = useState<TabFilter>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const selectedItemRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasEditableRef = useRef<boolean>(true);

  // Auto-focus search input when component mounts
  useEffect(() => {
    // Small delay to ensure the popup is fully rendered
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Disable editor when search input is focused to prevent TipTap from processing keyboard events
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input || !props.editor) return;

    const handleFocus = () => {
      // Save current editable state
      wasEditableRef.current = props.editor.isEditable;
      // Blur the editor to remove focus from it
      props.editor.view.dom.blur();
      // Disable editor to prevent it from processing keyboard events
      props.editor.setEditable(false);
    };

    const handleBlur = () => {
      // Re-enable editor when search input loses focus
      if (wasEditableRef.current) {
        props.editor.setEditable(true);
      }
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    // eslint-disable-next-line consistent-return
    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
      // Make sure to re-enable editor on cleanup
      if (wasEditableRef.current && props.editor) {
        props.editor.setEditable(true);
      }
    };
  }, [props.editor]);

  // Add mousedown handler to prevent Tippy from interfering with clicks
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;

    const handleMouseDownCapture = (e: MouseEvent) => {
      // If clicking on the input, prevent Tippy from handling it
      if (e.target === input || input.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };

    input.addEventListener('mousedown', handleMouseDownCapture, true);

    // eslint-disable-next-line consistent-return
    return () => {
      input.removeEventListener('mousedown', handleMouseDownCapture, true);
    };
  }, []);

  // Filter items based on tab selection and search query
  const filteredItems = useMemo(() => {
    // Apply tab filter first so each tab only sees its own tag (e.g. Talent tab only worker-tagged items)
    let items =
      tabFilter != null
        ? props.items.filter(item => item != null && item.tag === tabFilter)
        : [...props.items];

    // Deduplicate by (tag, id) so the same attribute appears once (e.g. "company name" not twice)
    const seenKeys = new Set<string>();
    items = items
      .filter(item => {
        if (!item || !item.id) return false;
        const key = `${item.tag ?? ''}-${item.id}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      })
      .map(item => ({ ...item }));

    // Then filter by search query
    if (searchQuery?.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(item => item.label.toLowerCase().includes(query));
    }

    return items;
  }, [props.items, tabFilter, searchQuery]);

  // Reset selected index when items or filter change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems.length, tabFilter, searchQuery]);

  // Scroll selected item into view when index changes
  useEffect(() => {
    if (selectedItemRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      });
    }
  }, [selectedIndex]);

  const selectItem = (index: number) => {
    const item = filteredItems[index];
    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + filteredItems.length - 1) % filteredItems.length,
    );
    return true;
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % filteredItems.length);
    return true;
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
    return true;
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      // Check if user is typing in search input
      const isTypingInSearch =
        document.activeElement === searchInputRef.current ||
        (event.target as HTMLElement)?.tagName === 'INPUT';

      // Handle arrow keys - always allow navigation
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        downHandler();
        return true;
      }

      // Handle Enter - select item
      if (event.key === 'Enter') {
        if (!isTypingInSearch) {
          event.preventDefault();
          event.stopPropagation();
          enterHandler();
          return true;
        }
        // If typing in search and Enter is pressed, select the item
        event.preventDefault();
        event.stopPropagation();
        enterHandler();
        return true;
      }

      // For other keys when typing in search, don't interfere
      // This allows normal typing in the search input
      if (
        isTypingInSearch &&
        !['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)
      ) {
        return false;
      }

      return false;
    },
  }));

  // Get unique tags for tabs
  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    props.items.forEach(item => {
      if (item.tag) {
        tags.add(item.tag);
      }
    });
    return Array.from(tags);
  }, [props.items]);

  const tabs = [
    {
      label: (
        <FormattedMessage
          defaultMessage="All"
          description="Merge-field picker tab: every attribute category"
        />
      ),
      filter: null as TabFilter,
    },
    {
      label: (
        <FormattedMessage
          defaultMessage="Talent"
          description="Tab: worker/applicant profile attributes only"
        />
      ),
      filter: 'worker' as TabFilter,
    },
    {
      label: (
        <FormattedMessage
          defaultMessage="Company"
          description="Tab: org-level company fields (for merge variables)"
        />
      ),
      filter: 'company' as TabFilter,
    },
    {
      label: (
        <FormattedMessage
          defaultMessage="Brand"
          description="Tab: brand-specific fields for the opening"
        />
      ),
      filter: 'brand' as TabFilter,
    },
  ].filter(tab => tab.filter === null || availableTags.includes(tab.filter));

  if (!props.items.length) {
    return (
      <div className="z-50 min-w-[360px] rounded-lg border border-gray-300 bg-white shadow-lg">
        <div className="px-3 py-1.5 text-sm text-gray-500">
          <FormattedMessage
            defaultMessage="No attributes found"
            description="Empty merge-field list when filters or search return nothing"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="z-50 flex w-[360px] flex-col overflow-hidden rounded-lg border border-gray-300 bg-white text-base shadow-lg"
      data-suggestion-list="true"
    >
      {/* Tabs */}
      <div className="flex flex-col gap-[5px] border-b border-gray-300 bg-gray-100 p-1">
        <div className="flex gap-2">
          {tabs.map(tab => {
            const tabKey = tab.filter ?? 'all';
            return (
              <button
                key={tabKey}
                type="button"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTabFilter(tab.filter);
                  setSearchQuery('');
                }}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={cn(
                  'flex-1 cursor-pointer rounded-md p-1 text-center transition-colors',
                  tabFilter === tab.filter ? 'bg-white shadow-sm' : '',
                )}
                style={{ pointerEvents: 'auto' }}
              >
                <span
                  className={cn(
                    'text-sm text-gray-900',
                    tabFilter === tab.filter ? 'font-medium' : 'font-normal',
                  )}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
        {/* Search Input */}
        <div className="px-1 pb-1">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={intl.formatMessage({
              defaultMessage: 'Filter by keyword',
              description:
                'Search box to narrow the merge attribute suggestion list',
            })}
            value={searchQuery}
            data-search-input="true"
            tabIndex={0}
            onChange={e => {
              // Update search query - this will trigger filtering
              e.stopPropagation();
              const { value } = e.target;
              setSearchQuery(value);
              // Reset selected index when search changes
              setSelectedIndex(0);
            }}
            onInput={e => {
              // Also handle input event to ensure it works
              e.stopPropagation();
            }}
            onKeyDown={e => {
              // Stop propagation to prevent TipTap from intercepting
              e.stopPropagation();

              // For normal typing keys, allow default behavior (don't prevent)
              if (
                !['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)
              ) {
                return;
              }

              // For arrow keys, navigate the list
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                upHandler();
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                downHandler();
              } else if (e.key === 'Enter') {
                // For Enter, select the item
                e.preventDefault();
                enterHandler();
              }
            }}
            onMouseDown={e => {
              // Stop propagation to prevent Tippy from handling
              e.preventDefault(); // Prevent default to ensure we can focus
              e.stopPropagation();
              // Blur editor first, then focus input
              if (props.editor) {
                props.editor.view.dom.blur();
              }
              // Use setTimeout to ensure focus happens after editor is blurred
              setTimeout(() => {
                searchInputRef.current?.focus();
              }, 0);
            }}
            onClick={e => {
              // Allow clicking to focus
              e.preventDefault();
              e.stopPropagation();
              // Blur editor first
              if (props.editor) {
                props.editor.view.dom.blur();
              }
              // Force focus
              setTimeout(() => {
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
              }, 0);
            }}
            onFocus={e => {
              // Allow focus events
              e.stopPropagation();
              // Ensure editor is blurred when input gets focus
              if (props.editor) {
                props.editor.view.dom.blur();
              }
              // Update focus state for border color
              setIsSearchFocused(true);
            }}
            onBlur={() => {
              // Reset focus state
              setIsSearchFocused(false);
            }}
            onPointerDown={e => {
              // Handle pointer events (touch/mouse)
              e.preventDefault();
              e.stopPropagation();
              if (props.editor) {
                props.editor.view.dom.blur();
              }
              setTimeout(() => {
                searchInputRef.current?.focus();
              }, 0);
            }}
            onTouchStart={e => {
              // Handle touch events
              e.stopPropagation();
            }}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-1"
            style={{
              pointerEvents: 'auto',
              cursor: 'text',
              userSelect: 'text',
              WebkitUserSelect: 'text',
              color: '#111827', // gray-900 equivalent
              borderColor: isSearchFocused ? '#3935FF' : undefined,
              boxShadow: isSearchFocused ? '0 0 0 1px #3935FF' : undefined,
            }}
          />
        </div>
      </div>

      {/* Items List */}
      <div
        className="max-h-48 overflow-y-auto overscroll-contain"
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
        onWheel={e => {
          // Stop propagation to prevent Tippy from interfering with scroll
          e.stopPropagation();
        }}
      >
        {filteredItems.length === 0 ? (
          <div className="px-3 py-1.5 text-left text-sm text-gray-500">
            <FormattedMessage
              defaultMessage="No attributes found"
              description="Empty merge-field list when filters or search return nothing"
            />
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <button
              key={`${item.tag ?? ''}-${item.id}`}
              ref={index === selectedIndex ? selectedItemRef : undefined}
              type="button"
              tabIndex={-1}
              className={cn(
                'w-full px-3 py-1.5 text-left transition-colors hover:bg-gray-50',
                index === selectedIndex ? 'bg-blue-50' : '',
              )}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                selectItem(index);
              }}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            >
              <div className="flex w-full justify-between text-sm">
                <span className="text-gray-900">{item.label}</span>
                <span className="text-gray-600">
                  {item.attrType ?? item.dataType}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
});
